import { useTheme } from '@emotion/react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useQuery } from 'react-query';

import { Dropdown } from 'shopper/components/Dropdown';
import Flex from 'shopper/components/Flex';
import Heading from 'shopper/components/Heading';
import Link from 'shopper/components/Link';

import { storeQueryKeys } from 'lib/queryKeys';
import { get } from 'lib/request';

import { useTopbar } from 'providers/TopbarProvider';

import StoresDropdownItem from './StoresDropdownItem';

import LINKS from 'constants/links';
import TOPBAR from 'constants/topbar';
import Z_INDEX_SCALE from 'constants/zIndexScale';

const Overlay = dynamic(() => import('shopper/components/Overlay'), {
  ssr: false,
});

const StoresDropdown = ({ isOpen, openerRef, onClose }) => {
  const { stores: initialData } = useTopbar();
  const theme = useTheme();
  const {
    data: stores,
    isFetched,
    isSuccess,
    refetch,
  } = useQuery(storeQueryKeys.list({ featured: true }), {
    queryFn: () => get('/store?from=topbar'),
    initialData,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (isOpen && !isFetched) {
      refetch();
    }
  }, [isFetched, isOpen, refetch]);

  if (!isSuccess) {
    return null;
  }

  return (
    <>
      {isOpen && <Overlay bg={theme.app.dropdown.overlay} full />}
      <Dropdown
        id="stores-dropdow"
        data-test-selector="stores-dropdown"
        align="left"
        border="1px solid"
        openerRef={openerRef}
        open={isOpen}
        borderColor={theme.app.storesDropdown.border}
        bg={theme.app.storesDropdown.bg}
        zIndex={Z_INDEX_SCALE.SIZE_6}
        onClickOutside={onClose}
        renderToHTML
      >
        <Flex
          flexDirection="column"
          overflowY="auto"
          overflowX="hidden"
          width={[null, null, null, '48rem', '60rem']}
          height={[null, null, null, '37.25rem', '32.25rem']}
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            borderBottom="1px solid"
            pb={2}
            mt={3}
            mx={6}
            borderColor={theme.app.storesDropdown.border}
          >
            <Heading size="size4">{TOPBAR.STORES}</Heading>
            <NextLink href={LINKS.STORES} passHref>
              <Link data-test-selector="goto-all-stores-list" onClick={onClose}>
                {TOPBAR.SHOW_ALL_STORES}
              </Link>
            </NextLink>
          </Flex>
          {stores && (
            <Flex
              flexDirection="column"
              flexWrap="wrap"
              overflowY="scroll"
              gap="10px"
              mt={4}
              height={[null, null, null, '33rem', '28rem']}
              overflow="auto"
            >
              {stores.map((store) => (
                <StoresDropdownItem
                  key={store.pageTitle}
                  data-test-selector="store-dropdown-item"
                  showStoreImage={isOpen}
                  onClick={onClose}
                  {...store}
                />
              ))}
            </Flex>
          )}
        </Flex>
      </Dropdown>
    </>
  );
};

StoresDropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  openerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.instanceOf(
        typeof Element === 'undefined' ? () => {} : Element
      ),
    }),
  ]).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default StoresDropdown;
